import React, {useLayoutEffect,useRef,useState} from "react";
import axios from "src/common/axiosInstance";
import CustomerDataPicker from "src/common/customerSharing/data/CustomerDataPicker";
import { Mic, Send, Braces } from "lucide-react";
import QuickReplyPicker from "src/features/QuickReplies/QuickReplyPicker";
import EmojiPickerPopover from "./component/EmojiPickerPopover";
import AttachmentMenu from "./component/AttachmentMenu";
import { FilePreview, ReplyPreview } from "./component/Preview";
import ComposerActions from "./component/ComposerActions";
import useMessageComposer from "../../hooks/useMessageComposer";



const loadCustomerContext=(id,params,signal)=>axios.get(`/customer-context/records/${encodeURIComponent(id)}`,{params,signal}).then(r=>r.data.data);

function MessageComposer({conversation,onMessageSent,replyTo,onCancelReply,onTemplateClick}) {
  const {
    message,
    setMessage,
    sending,
    showEmojiPicker,
    closeEmojiPicker,
    closeAttachmentMenu,
    showAttachmentMenu,
    selectedFile,
    fileInputRef,
    canSend,
    handleEmojiClick,
    handleFileChange,
    removeFile,
    openFilePicker,
    getReplyPreview,
    handleSend,
    handleKeyDown,
    toggleEmojiPicker,
    toggleAttachmentMenu,
  } = useMessageComposer({conversation, onMessageSent, onCancelReply, replyTo});

  const [dataOpen,setDataOpen]=useState(false),[inserted,setInserted]=useState(''),[repliesOpen,setRepliesOpen]=useState(false);
  const inputRef=useRef(null),range=useRef({start:0,end:0});
  const contactId=conversation?.whatsapp_contact_id || conversation?.contact?.id;
  // AUTO-WA-39: the box grows with the text (up to six lines) so long replies can be read before sending.
  useLayoutEffect(()=>{const box=inputRef.current;if(!box)return;box.style.height="auto";box.style.height=`${Math.min(box.scrollHeight,132)}px`;},[message]);
  // AUTO-WA-39: a saved answer goes into the box for review; it is never sent on its own.
  const insertReply=body=>{setMessage(message.trim()?`${message.trimEnd()} ${body}`:body);setInserted('Quick reply added. Check it, then send.');requestAnimationFrame(()=>inputRef.current?.focus());};
  const insertField=field=>{const {start,end}=range.current;setMessage(message.slice(0,start)+field.value+message.slice(end));setInserted(`Inserted ${field.label} from ${field.source}. Review before sending.`);setDataOpen(false);requestAnimationFrame(()=>{inputRef.current?.focus();inputRef.current?.setSelectionRange(start+field.value.length,start+field.value.length);});};
  return (
  <div className="wa-composer">

    {dataOpen&&contactId&&<CustomerDataPicker recordId={contactId} loadContext={loadCustomerContext} onInsert={insertField} onClose={()=>setDataOpen(false)}/>}
    {repliesOpen&&<QuickReplyPicker onInsert={insertReply} onClose={()=>setRepliesOpen(false)}/>}
    {inserted&&<p className="wa-composer-note" role="status">{inserted}</p>}
    <EmojiPickerPopover
      open={showEmojiPicker}
      onEmojiClick={handleEmojiClick}
      onClose={closeEmojiPicker}
    />

    <AttachmentMenu
      open={showAttachmentMenu}
      onSelectAttachment={openFilePicker}
      onClose={closeAttachmentMenu}
    />

    <ReplyPreview
      replyTo={replyTo}
      conversation={conversation}
      onCancel={onCancelReply}
      getReplyPreview={getReplyPreview}
    />

    <FilePreview
      file={selectedFile}
      onRemove={removeFile}
    />

    <input
      ref={fileInputRef}
      type="file"
      className="hidden"
      onChange={handleFileChange}
    />

    {/* =====================================================
        COMPOSER
    ====================================================== */}
    <div className="wa-composer-row">

      {contactId&&<button type="button" aria-label="Insert customer data" title="Customer data · type /" disabled={sending} onClick={()=>setDataOpen(true)} className="wa-tool"><Braces size={17}/></button>}
      {/* Composer Actions */}
      <div className="shrink-0">
        <ComposerActions
          showAttachmentMenu={showAttachmentMenu}
          sending={sending}
          onEmojiClick={toggleEmojiPicker}
          onAttachmentClick={toggleAttachmentMenu}
          onTemplateClick={onTemplateClick}
          onQuickRepliesClick={()=>setRepliesOpen((open)=>!open)}
          quickRepliesOpen={repliesOpen}
        />
      </div>

      {/* =================================================
          MESSAGE INPUT
      ================================================= */}
      <div className="wa-composer-input">

        <textarea
          rows={1}
          ref={inputRef}
          onSelect={e=>{range.current={start:e.target.selectionStart,end:e.target.selectionEnd};}}
          value={message}
          disabled={sending}
          onChange={event=>{setMessage(event.target.value);const caret=event.target.selectionStart;range.current={start:caret,end:caret};if(contactId&&/(?:^|\s)\/$/.test(event.target.value.slice(0,caret))){range.current={start:caret-1,end:caret};setDataOpen(true);}}}
          onKeyDown={handleKeyDown}
          placeholder={
            selectedFile
              ? "Add a caption..."
              : "Write a reply…"
          }
          aria-label="Message"
        />

      </div>

      {/* =================================================
          SEND / VOICE BUTTON
      ================================================= */}
      <button
        type="button"
        disabled={(!canSend && !sending) || sending}
        onClick={handleSend}
        className={`wa-send${canSend && !sending ? " is-ready" : ""}`}
        title={canSend ? "Send" : "Voice message"}
        aria-label={canSend ? "Send" : "Voice message"}
      >
        {canSend ? (
          <Send
            size={17}
            strokeWidth={2}
            className="translate-x-[1px] sm:h-[18px] sm:w-[18px]"
          />
        ) : (
          <Mic
            size={19}
            strokeWidth={1.9}
            className="sm:h-5 sm:w-5"
          />
        )}
      </button>

    </div>
  </div>
);
}

export default MessageComposer;
